import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Notices tab: write an announcement ("Binance API problem fixed.") and post it
 * to the shop channel, send it to every bot buyer, or both.
 */
function TelegramNoticesAdmin() {
    const [info, setInfo] = useState(null);
    const [text, setText] = useState('');
    const [toChannel, setToChannel] = useState(true);
    const [toBuyers, setToBuyers] = useState(false);
    const [shopButton, setShopButton] = useState(true);
    const [sending, setSending] = useState(false);
    const [notice, setNotice] = useState(null);
    const [error, setError] = useState(null);

    const authFetch = useCallback(async (path, options = {}) => {
        const token = localStorage.getItem('adminToken');
        const res = await fetch(`${API_URL}${path}`, {
            ...options,
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(options.headers || {}) },
        });
        return { ok: res.ok, body: await res.json().catch(() => ({})) };
    }, []);

    const load = useCallback(async () => {
        const { ok, body } = await authFetch('/admin/telegram/notices');
        if (ok) setInfo(body);
    }, [authFetch]);

    useEffect(() => { load(); }, [load]);

    // While buyers are being messaged, follow the progress.
    const job = info && info.job;
    const sendingToBuyers = job && job.state === 'sending';
    useEffect(() => {
        if (!sendingToBuyers) return undefined;
        const t = setInterval(load, 3000);
        return () => clearInterval(t);
    }, [sendingToBuyers, load]);

    const send = async () => {
        if (!text.trim()) { setError('Write the notice first.'); return; }
        const where = [toChannel && `the channel @${info?.channel || '?'}`, toBuyers && `${info?.buyers || 0} buyers`].filter(Boolean).join(' and ');
        if (!where) { setError('Tick the channel, the buyers, or both.'); return; }
        if (!window.confirm(`Send this notice to ${where}?\n\n${text.trim()}`)) return;
        setSending(true); setError(null); setNotice(null);
        const { ok, body } = await authFetch('/admin/telegram/notices', {
            method: 'POST', body: JSON.stringify({ text, toChannel, toBuyers, shopButton })
        });
        setSending(false);
        if (!ok) { setError(body.error || 'Could not send.'); return; }
        const r = body.record || {};
        const parts = [];
        if (r.channel) parts.push(r.channel.ok ? `posted in @${r.channel.channel}` : `channel failed: ${r.channel.error}`);
        if (r.buyers) parts.push(`sending to ${r.buyers.total} buyers…`);
        setNotice(`Notice ${parts.join(' · ')}`);
        setText('');
        load();
    };

    const when = iso => iso ? new Date(iso).toLocaleString() : '';

    return (
        <div>
            {notice && <div className="tg-note ok">{notice}</div>}
            {error && <div className="tg-note bad">{error}</div>}

            <div className="tg-card">
                <h3>Send a notice</h3>
                <p className="tg-sub">
                    Post an announcement in your channel, message every bot buyer directly, or both.
                    Buyers see it as <b>📢 Notice</b> in their own language, followed by your text exactly as written.
                </p>

                {info && info.channel && !info.canPost && (
                    <div className="tg-note bad" style={{ marginBottom: 12 }}>
                        <b>The bot cannot post in @{info.channel} yet.</b> {info.detail}
                    </div>
                )}

                <textarea className="tg-input" rows={5} style={{ width: '100%', boxSizing: 'border-box' }} value={text}
                    onChange={e => setText(e.target.value)} maxLength={3500}
                    placeholder="e.g. Binance API problem fixed. Deposits are working again." />
                <div style={{ fontSize: 12, opacity: 0.6, textAlign: 'right' }}>{text.length} / 3500</div>

                <div className="tg-flags" style={{ margin: '10px 0' }}>
                    <label>
                        <input type="checkbox" checked={toChannel} onChange={e => setToChannel(e.target.checked)} />
                        {' '}Post in the channel {info && info.channel ? <b>@{info.channel}</b> : <i>(no channel set)</i>}
                        {info && info.channel && info.canPost && <span style={{ color: '#16a34a', marginLeft: 6 }}>✓ ready</span>}
                    </label>
                    <label>
                        <input type="checkbox" checked={toBuyers} onChange={e => setToBuyers(e.target.checked)} />
                        {' '}Message every bot buyer <b>({(info?.buyers || 0).toLocaleString()})</b>
                        <span style={{ display: 'block', fontWeight: 400, fontSize: 12, opacity: 0.7, marginTop: 2 }}>
                            Goes to each buyer's chat with the bot — about {Math.max(1, Math.round((info?.buyers || 0) / 20 / 60))} min for everyone.
                            Blocked buyers are skipped.
                        </span>
                    </label>
                    <label>
                        <input type="checkbox" checked={shopButton} onChange={e => setShopButton(e.target.checked)} />
                        {' '}Add a "🛍 Shop" button under the notice
                    </label>
                </div>

                {text.trim() && (
                    <div style={{ margin: '8px 0 14px' }}>
                        <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>Preview</div>
                        <div style={{ display: 'inline-block', maxWidth: 420, padding: '10px 14px', borderRadius: 12, background: 'rgba(37,99,235,0.08)', whiteSpace: 'pre-wrap' }}>
                            📢 <b>Notice</b>{'\n\n'}{text.trim()}
                            {shopButton && <div style={{ marginTop: 10, textAlign: 'center', padding: 6, borderRadius: 8, background: 'rgba(0,0,0,0.06)' }}>🛍 Shop</div>}
                        </div>
                    </div>
                )}

                <button className="tg-btn primary" disabled={sending || sendingToBuyers} onClick={send}>
                    {sending ? 'Sending…' : sendingToBuyers ? 'A notice is still going out…' : 'Send notice'}
                </button>
            </div>

            {job && (
                <div className="tg-card">
                    <h3>{job.state === 'sending' ? 'Sending to buyers…' : 'Last send to buyers'}</h3>
                    <div style={{ height: 10, borderRadius: 6, background: 'rgba(0,0,0,0.08)', overflow: 'hidden', margin: '6px 0 8px' }}>
                        <div style={{ height: '100%', width: `${job.total ? Math.round(100 * (job.sent + job.failed) / job.total) : 100}%`, background: '#16a34a' }} />
                    </div>
                    <div className="tg-row-meta">
                        {job.sent + job.failed} of {job.total} · delivered <b>{job.sent}</b>
                        {job.failed > 0 && <> · {job.failed} not delivered (they blocked or deleted the bot)</>}
                        {' · '}started {when(job.startedAt)}{job.finishedAt && <> · finished {when(job.finishedAt)}</>}
                    </div>
                    {job.preview && <div className="tg-row-meta" style={{ marginTop: 4, fontStyle: 'italic' }}>“{job.preview}{job.preview.length >= 80 ? '…' : ''}”</div>}
                </div>
            )}

            {info && info.history && info.history.length > 0 && (
                <div className="tg-card">
                    <h3>Recent notices</h3>
                    {info.history.map(h => (
                        <div key={h.at} className="tg-row">
                            <div className="tg-row-main">
                                <div style={{ whiteSpace: 'pre-wrap' }}>{h.text}</div>
                                <div className="tg-row-meta" style={{ marginTop: 4 }}>
                                    {when(h.at)} · by {h.by}
                                    {h.channel && <> · channel: {h.channel.ok ? `posted in @${h.channel.channel}` : `failed (${h.channel.error})`}</>}
                                    {h.buyers && <> · buyers: {h.buyers.sent !== undefined ? `${h.buyers.sent}/${h.buyers.total} delivered` : `${h.buyers.total} (sending)`}</>}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default TelegramNoticesAdmin;
